/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';

import Prerequisites from 'bundles/degree-home/components/v3-dpm/components/CourseCard/Prerequisites';
import UnknownAvailabilityDescription from 'bundles/degree-home/components/v3-dpm/components/CourseCard/UnknownAvailabilityDescription';
import AcademicTermsCourseCardActions from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/AcademicTermsCourseCardActions';
import type { TermStatus } from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/lib/academicTermTypes';
import { useLearnerPlanStoreContext } from 'bundles/degree-home/components/v3-dpm/context/LearnerPlanStoreContext';
import type { UseCurriculum_PersonalizedSegmentCourseFragment as SegmentCourse } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';
import { roundGrade } from 'bundles/degree-home/components/v3-dpm/lib/gradeUtils';
import { checkCourseHasUnknownAvailability } from 'bundles/degree-home/components/v3-dpm/lib/otherUtils';
import CreditCount from 'bundles/program-loggedin-shared/components/CreditCount';
import { METADATA_SEPARATOR, MetadataTypography } from 'bundles/program-loggedin-shared/components/Metadata';
import RequirementType from 'bundles/program-loggedin-shared/components/RequirementType';

import _t from 'i18n!nls/degree-home';

export type Props = {
  termStatus: TermStatus;
  course: SegmentCourse;
  termId?: string;
};

const styles = {
  content: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--cds-spacing-100);
    align-self: stretch;
  `,
  card: css`
    padding: var(--cds-spacing-100);
    gap: var(--cds-spacing-100);
    border: none;
  `,
};

const AcademicTermsCourseCard: React.VFC<Props> = ({ termId, course, termStatus }) => {
  const { segmentCourse, learnerProgress } = course;
  const { requirementTypeData, plannedCoursesNeedReplanData } = useLearnerPlanStoreContext();

  const courseHasUnknownAvailability = checkCourseHasUnknownAvailability(segmentCourse);

  // fail gracefully if requirementTypeData is undefined
  const isElective = requirementTypeData?.[course.segmentCourse.id]?.isElective;
  const { courseCode } = segmentCourse.course;
  const courseName = segmentCourse.course.courseName;

  const { grade } = learnerProgress;

  const isReplanNeeded = !!plannedCoursesNeedReplanData?.[segmentCourse.id];

  return (
    <React.Fragment>
      <div css={styles.card}>
        <div css={styles.content}>
          <Typography2 component="h3" variant="subtitleMedium">
            {_t('#{courseCode} - #{courseName}', {
              courseName,
              courseCode,
            })}
          </Typography2>
          <div>
            <Prerequisites prerequisite={segmentCourse.prerequisite} />

            {isElective !== undefined && (
              <React.Fragment>
                <RequirementType isElective={isElective} key="RequirementType" />
                {METADATA_SEPARATOR}
              </React.Fragment>
            )}

            <CreditCount credits={segmentCourse.creditValue} />

            {courseHasUnknownAvailability && <UnknownAvailabilityDescription withMetadataSeparator />}

            {grade && (
              <React.Fragment>
                {METADATA_SEPARATOR}
                <MetadataTypography inline color="success" key="Grade">
                  {_t('Grade: #{grade}%', { grade: roundGrade(grade) })}
                </MetadataTypography>
              </React.Fragment>
            )}

            {isReplanNeeded && (
              <MetadataTypography color="error">
                {_t('This course is no longer offered in this academic term. Please plan to take in a different term.')}
              </MetadataTypography>
            )}
          </div>
        </div>
      </div>

      <AcademicTermsCourseCardActions
        termId={termId}
        course={course}
        hideEnrollmentOptions={isReplanNeeded}
        termStatus={termStatus}
      />
    </React.Fragment>
  );
};

export default AcademicTermsCourseCard;
